import {useQueries} from '@tanstack/react-query';

import {zonesKeys} from './useZonesQuery';
import * as ZonesService from '../services/ZonesService';
import {
    DnsZone,
    ZoneWriteCapabilities,
} from '../types';

// FNP-1446 — WHICH CONNECTION ANSWERS "CAN I DELETE THIS ZONE?"
//
// FNP-1378 grouped the list by real zone, so one row can be reached through
// several connections. It kept the group's `representative` as the first row in
// fetch order and deliberately left the mutation-connection question open. The
// capability CHECK then inherited that arbitrary choice: `DeleteZoneTool` asked
// `representative.connectionId`, so a zone reachable through a delete-capable
// connection hid its Delete button whenever some other connection happened to
// be fetched first.
//
// ⚠️ THE QUESTION IS "CAN ANY CONNECTION DELETE THIS ZONE", NOT "CAN THIS ONE".
// Capabilities are per connection on purpose — a tenant can hold a writable
// Route 53 connection and a read-only Cloudflare one, and grouping did not
// change that. It changed how many connections one row stands for. Asking one
// of them at random turns a real per-connection fact into a coin flip, and the
// failure is invisible: a hidden button looks exactly like a correct read-only
// refusal.
//
// ⚠️ THE ANSWER CARRIES THE ROW, NOT JUST A BOOLEAN. Whatever connection
// satisfied the check is the connection the delete must go through, or the
// check and the action are talking about different credentials. Returning the
// capable `DnsZone` makes that impossible to get wrong downstream — the caller
// cannot accidentally keep using the representative.
//
// ⚠️ FIRST CAPABLE IN FETCH ORDER, WHICH IS DETERMINISTIC, NOT ARBITRARY. Fetch
// order is stable across renders (the grouping Map preserves insertion order),
// so the same zone resolves to the same connection every time. Preferring one
// capable connection over another on some other basis — least privilege, most
// recently validated — is a product decision this ticket does not make.

/**
 * The rows of one grouped zone, resolved to whichever one may be deleted.
 *
 * Pass every `DnsZone` that grouped together. Pass an empty array when nothing
 * is selected, or when more than one row is — neither has an unambiguous
 * answer, and the caller renders a disabled control rather than asking.
 */
export const useZoneGroupDeleteTarget = (zones: DnsZone[] = []) => {

    // One query per connection reaching the zone. They share `zonesKeys
    // .capabilities(...)` with `useZoneCapabilitiesQuery`, so a connection
    // already read elsewhere on the page is served from cache rather than
    // refetched — the ordinary single-connection zone costs nothing new.
    const results = useQueries({
        queries: zones.map(zone => ({
            queryKey: zonesKeys.capabilities(zone.connectionId),
            queryFn: async (): Promise<ZoneWriteCapabilities | null> => {
                return await ZonesService.fetchConnectionCapabilities(
                    zone.connectionId,
                ) ?? null;
            },
            enabled: Boolean(zone.connectionId),
            staleTime: 300_000,
        })),
    });

    // ⚠️ EVERY UNKNOWN COUNTS AS "NOT PERMITTED", exactly as
    // `useZoneWriteCapabilities` does. Loading, errored and absent-snapshot all
    // fail closed; the default must not soften just because there are now
    // several connections to be unsure about.
    const capableIndex = results.findIndex(
        (result, index) =>
            Boolean(zones[index]?.connectionId)
            && !result.isLoading
            && !result.isError
            && result.data?.canDeleteZones === true,
    );

    const loading = zones.length > 0 && results.some(result => result.isLoading);

    return {
        /**
         * The row to delete THROUGH, or undefined when no connection reaching
         * this zone may delete it. This is what the mutation must be given.
         */
        deleteTarget: capableIndex === -1 ? undefined : zones[capableIndex],
        /** True once some connection reaching this zone has answered yes. */
        canDeleteZones: capableIndex !== -1,
        /** Any connection still being asked. Callers hide rather than promise. */
        loading,
    };
};
